import { useCallback, useEffect, useState } from "react";
import axios from "axios";

// Per-tab lazy data loader. A tab component calls useTabData(url) and the data is
// fetched the first time the tab is shown, cached for the page's lifetime, and
// reused on revisit — so the initial page load only pays for the default tab.
// Pass a falsy url to skip fetching (e.g. a tab whose data is already in props).
const cache = new Map();
const generations = new Map();

export const forgetTabData = (url) => {
  if (!url) return;
  cache.delete(url);
  generations.set(url, (generations.get(url) || 0) + 1);
};

export default function useTabData(url) {
  const [state, setState] = useState(() => (
    url && cache.has(url)
      ? { loading: false, data: cache.get(url), error: null }
      : { loading: Boolean(url), data: null, error: null }
  ));
  useEffect(() => {
    if (!url || cache.has(url)) {
      if (url) setState({ loading: false, data: cache.get(url), error: null });
      return undefined;
    }

    let active = true;
    const load = () => {
      const generation = generations.get(url) || 0;
      axios.get(url)
        .then((res) => {
          if ((generations.get(url) || 0) !== generation) {
            if (active) load();
            return;
          }
          cache.set(url, res.data);
          if (active) setState({ loading: false, data: res.data, error: null });
        })
        .catch((error) => {
          if ((generations.get(url) || 0) !== generation) {
            if (active) load();
            return;
          }
          if (active) setState({ loading: false, data: null, error });
        });
    };
    setState({ loading: true, data: null, error: null });
    load();

    return () => { active = false; };
  }, [url]);

  // Lets a tab write fresh data back into the cache after a mutation, so a
  // remount on tab revisit re-seeds from the latest state instead of the
  // original payload. Stable per-url so consumers can use it in effect deps.
  const setData = useCallback((updater) => {
    setState((prev) => {
      const data = typeof updater === "function" ? updater(prev.data) : updater;
      if (url) cache.set(url, data);
      return { ...prev, data };
    });
  }, [url]);

  return { ...state, setData };
}
